import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { useHistory, useParams } from "react-router-dom";
import {
  getSaleBillById,
  getSaleBillPaymentsById,
  updateSaleBillPayments,
  billSendingForApproval,
  approveBill,
} from "../../../services/Sales";
import FormAlert from "../../../components/FormAlert";
import BillItemDetailsDialog from "../../../components/BillItemDetailsDialog";
import PaymentOptions from "../../../components/PaymentOptions";
import PaymentListForConfirmation from "../../../components/PaymentListForConfimation";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import InventoryIcon from "@mui/icons-material/Inventory";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import { RoleBasedRender } from "../../../components/RoleBaseRender";

// Styled Components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));


const InfoCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  height: "100%",
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  boxShadow: "0 2px 8px 0 rgba(0,0,0,0.05)",
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  background: alpha(theme.palette.success.main, 0.05),
  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.05)",
}));

function SaleBillView() {
  const theme = useTheme();
  const history = useHistory();
  const { billId } = useParams();

  const [billData, setBillData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    message: "",
  });

  // Item view dialog state
  const [openItemView, setOpenItemView] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Payment state
  const [existingPayments, setExistingPayments] = useState([]);
  const [newPayments, setNewPayments] = useState([]);
  const [enablePayment, setEnablePayment] = useState(false);
  const [paymentAlert, setPaymentAlert] = useState({ show: false, type: "", message: "" });

  useEffect(() => {
    fetchBillData();
  }, [billId]);

  const fetchBillData = async () => {
    setIsLoading(true);
    try {
      const [result, paymentsResult] = await Promise.all([
        getSaleBillById(billId),
        getSaleBillPaymentsById(billId),
      ]);

      if (result && result !== "Something went wrong...") {
        setBillData(result);
      } else {
        setAlertData({ type: "error", message: "Failed to load bill data" });
        setAlert(true);
      }

      if (paymentsResult && paymentsResult !== "Something went wrong...") {
        setExistingPayments(paymentsResult);
      }
    } catch (error) {
      console.error("Error fetching bill:", error);
      setAlertData({ type: "error", message: "An error occurred while loading the bill" });
      setAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSave = async () => {
    try {
      const submitResult = await updateSaleBillPayments(billId, newPayments);
      if (submitResult !== "Something went wrong...") {
        setEnablePayment(false);
        setNewPayments([]);
        fetchBillData();
      } else {
        setPaymentAlert({ show: true, type: "error", message: "Failed to update payments." });
      }
    } catch (error) {
      setPaymentAlert({ show: true, type: "error", message: "Error saving payments." });
    }
  };

  const closeAlert = () => {
    setAlert(false);
    setAlertData({ type: "", message: "" });
  };

  const handleSendForApproval = async () => {
    setIsSubmitting(true);
    try {
      const result = await billSendingForApproval(billId);

      if (result && result !== "Something went wrong...") {
        setAlertData({
          type: "success",
          message: "Bill sent for approval successfully!",
        });
        setAlert(true);

        // Refresh bill data
        setTimeout(() => {
          fetchBillData();
        }, 1000);
      } else {
        setAlertData({
          type: "error",
          message: "Failed to send bill for approval",
        });
        setAlert(true);
      }
    } catch (error) {
      console.error("Error sending for approval:", error);
      setAlertData({
        type: "error",
        message: "An error occurred while sending for approval",
      });
      setAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApproveBill = async () => {
    setIsSubmitting(true);
    try {
      const result = await approveBill(billId, true);

      if (result && result !== "Unable to approve the bill...") {
        setAlertData({
          type: "success",
          message: "Bill approved successfully!",
        });
        setAlert(true);

        // The approveBill function already redirects, but we can refresh just in case
        setTimeout(() => {
          fetchBillData();
        }, 1000);
      } else {
        setAlertData({
          type: "error",
          message: "Failed to approve bill",
        });
        setAlert(true);
      }
    } catch (error) {
      console.error("Error approving bill:", error);
      setAlertData({
        type: "error",
        message: "An error occurred while approving the bill",
      });
      setAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectBill = async () => {
    setIsSubmitting(true);
    try {
      const result = await approveBill(billId, false);

      if (result && result !== "Unable to approve the bill...") {
        setAlertData({
          type: "success",
          message: "Bill rejected successfully",
        });
        setAlert(true);

        // Refresh bill data
        setTimeout(() => {
          fetchBillData();
        }, 1000);
      } else {
        setAlertData({
          type: "error",
          message: "Failed to reject bill",
        });
        setAlert(true);
      }
    } catch (error) {
      console.error("Error rejecting bill:", error);
      setAlertData({
        type: "error",
        message: "An error occurred while rejecting the bill",
      });
      setAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewItem = (item) => {
    setSelectedItem(item);
    setOpenItemView(true);
  };

  const handleCloseItemView = () => {
    setOpenItemView(false);
    setSelectedItem(null);
  };

  const formatCurrency = (value) => {
    return `Rs. ${parseFloat(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "success";
      case "REJECTED":
        return "error";
      case "AWAITING":
        return "warning";
      case "PENDING":
        return "info";
      case "SETTLED":
        return "primary";
      case "DRAFT":
        return "default";
      default:
        return "default";
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEdit = () => {
    history.push(`/template/sale_bill_edit/${billId}`);
  };

  if (isLoading) {
    return (
      <StyledPaper>
        <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
          <CircularProgress />
        </Box>
      </StyledPaper>
    );
  }

  if (!billData) {
    return (
      <StyledPaper>
        <Box sx={{ textAlign: "center", p: 5 }}>
          <Typography variant="h6" color="error">
            Bill not found
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.goBack()}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Box>
      </StyledPaper>
    );
  }

  return (
    <StyledPaper>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ReceiptIcon fontSize="large" color="primary" />
          <Box>
            <Typography variant="h5" fontWeight="600" color="primary.main">
              Bill #{billData.billNumber}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip
                icon={
                  billData.billCategory === "RETAIL" ? (
                    <StorefrontIcon />
                  ) : (
                    <BusinessIcon />
                  )
                }
                label={billData.billCategory}
                size="small"
                color={
                  billData.billCategory === "RETAIL" ? "primary" : "secondary"
                }
              />
              <Chip
                label={billData.status}
                size="small"
                color={getStatusColor(billData.status)}
              />
            </Stack>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Print Bill">
            <IconButton
              onClick={handlePrint}
              sx={{
                bgcolor: alpha(theme.palette.grey[500], 0.1),
                "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.2) },
              }}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>
          {billData.status === "DRAFT" && (
            <Tooltip title="Edit Bill">
              <IconButton
                onClick={handleEdit}
                sx={{
                  bgcolor: alpha(theme.palette.grey[500], 0.1),
                  "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.2) },
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Go Back">
            <IconButton
              onClick={() => history.goBack()}
              aria-label="go back"
              sx={{
                bgcolor: alpha(theme.palette.grey[500], 0.1),
                "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.2) },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Divider sx={{ mb: 4 }} />

      {/* Alert */}
      {displayAlert && (
        <Box sx={{ mb: 3 }}>
          <FormAlert type={alertData.type} message={alertData.message} />
        </Box>
      )}

      {/* Bill Information Grid */}
      <Grid container spacing={3}>
        {/* Customer Information */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Customer Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon color="primary" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Customer Name
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {billData.customerName || "N/A"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PhoneIcon color="primary" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Contact Number
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {billData.customerContactNumber || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </InfoCard>
        </Grid>

        {/* Bill Details */}
        <Grid item xs={12} md={6}>
          <InfoCard>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Bill Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarTodayIcon color="primary" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Bill Date
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {formatDate(billData.sellingDate)}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon color="primary" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Added By
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {billData.addedBy || "N/A"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <InventoryIcon color="primary" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Items
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {billData.totalItems || 0}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </InfoCard>
        </Grid>

        {/* Bill Items Table */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Bill Items
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: alpha(
                          billData.billCategory === "RETAIL"
                            ? "#1976d2"
                            : "#9c27b0",
                          0.1,
                        ),
                      }}
                    >
                      <TableCell>
                        <strong>Product</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Barcode</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Quantity</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Unit Price</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Subtotal</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Discount</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Total</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Profit</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {billData.billItems && billData.billItems.length > 0 ? (
                      billData.billItems.map((item, index) => (
                        <TableRow
                          key={item.itemId || index}
                          sx={{
                            "&:hover": {
                              bgcolor: alpha("#000", 0.02),
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {item.productName || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.barcode || "N/A"}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={item.billQty || 0}
                              size="small"
                              color={
                                billData.billCategory === "RETAIL"
                                  ? "primary"
                                  : "secondary"
                              }
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatCurrency(item.pricePerItem)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {formatCurrency(item.pricePerItem * item.billQty)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {item.discountPerItem > 0 ? (
                              <Typography
                                variant="body2"
                                color="error.main"
                                fontWeight="600"
                              >
                                -{formatCurrency(item.discountPerItem)}
                              </Typography>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="600">
                              {formatCurrency(item.price)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="600"
                              color={
                                item.itemProfit >= 0
                                  ? "success.main"
                                  : "error.main"
                              }
                            >
                              {formatCurrency(item.itemProfit)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Item Details">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleViewItem(item)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={9} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No items found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Financial Summary */}
        <Grid item xs={12} md={6}>
          <SummaryCard>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              Financial Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Discount Amount
                </Typography>
                <Typography variant="h6" color="error.main" fontWeight="600">
                  {billData.discountAmount > 0
                    ? `-${formatCurrency(billData.discountAmount)}`
                    : formatCurrency(0)}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body1" fontWeight="600">
                  Total Amount
                </Typography>
                <Typography variant="h5" color="primary.main" fontWeight="700">
                  {formatCurrency(billData.totalPrice)}
                </Typography>
              </Box>
              <Divider />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Total Profit
                </Typography>
                <Typography
                  variant="h6"
                  color={
                    billData.totalProfit >= 0 ? "success.main" : "error.main"
                  }
                  fontWeight="600"
                >
                  {formatCurrency(billData.totalProfit)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Remaining Amount
                </Typography>
                <Typography variant="h6" fontWeight="600">
                  {formatCurrency(billData.remainingAmount)}
                </Typography>
              </Box>
            </Stack>
          </SummaryCard>
        </Grid>

        {/* Notes */}
        {billData.note && (
          <Grid item xs={12} md={6}>
            <InfoCard>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Additional Notes
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {billData.note}
              </Typography>
            </InfoCard>
          </Grid>
        )}
      </Grid>

      {/* Payments */}
      <Card sx={{ mt: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PaymentIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="subtitle1" fontWeight="700" color="primary.main">
              Payments
            </Typography>
          </Box>

          {existingPayments.length > 0 && !enablePayment && (
            <PaymentListForConfirmation
              payments={existingPayments.map((p) => ({ ...p, type: p.paymentType }))}
            />
          )}

          {existingPayments.length === 0 && !enablePayment && (
            <Typography color="text.secondary" variant="body2">
              No payments recorded yet.
            </Typography>
          )}

          {enablePayment && (
            <>
              <PaymentOptions
                paymentMethods={newPayments}
                setPaymentMethods={setNewPayments}
                billType={"SALE"}
                paymentTypes={[
                  { value: "CASH", label: "Cash" },
                  { value: "CHEQUE", label: "Cheque" },
                  { value: "BILL", label: "Recover Import Bill" },
                  { value: "RECEIPT", label: "Receipt" },
                ]}
              />
              {paymentAlert.show && (
                <Alert
                  severity={paymentAlert.type}
                  onClose={() => setPaymentAlert({ show: false, type: "", message: "" })}
                  sx={{ mt: 2 }}
                >
                  {paymentAlert.message}
                </Alert>
              )}
              <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    setEnablePayment(false);
                    setNewPayments([]);
                    setPaymentAlert({ show: false, type: "", message: "" });
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PaymentIcon />}
                  onClick={handlePaymentSave}
                  disabled={newPayments.length === 0}
                >
                  Save Payments
                </Button>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Back to List
        </Button>

        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print Bill
        </Button>

        {/* Add Payment — visible when bill has a remaining balance and is past DRAFT/AWAITING */}
        {!["DRAFT", "AWAITING", "REJECTED"].includes(billData.status) &&
          parseFloat(billData.remainingAmount) > 0 && (
            <RoleBasedRender allowedRoles={["SALE_BILL_PAYMENT_UPDATE", "ROLE_ADMIN"]}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<PaymentIcon />}
                onClick={() => {
                  const converted = existingPayments.map((p) => ({
                    type: p.paymentType,
                    amount: p.amount,
                    bank: p.bank,
                    chequeNumber: p.chequeNumber,
                    returnDate: p.returnDate,
                    returnDateInput: p.returnDate != null ? dayjs(p.returnDate) : null,
                    receiptNumber: p.receiptNumber,
                    shopName: p.shopName,
                    returnBillNumber: p.returnBillNumber,
                  }));
                  setNewPayments(converted);
                  setEnablePayment(true);
                }}
              >
                Add Payment
              </Button>
            </RoleBasedRender>
          )}

        {/* Show "Send for Approval" only for DRAFT bills */}
        {billData.status === "DRAFT" && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendForApproval}
            disabled={isSubmitting}
            color="primary"
          >
            {isSubmitting ? "Sending..." : "Send for Approval"}
          </Button>
        )}

        {/* Show "Approve" and "Reject" only for AWAITING bills */}
        {billData.status === "AWAITING" && (
          <>
            <RoleBasedRender allowedRoles={["SALE_BILL_APPROVE", "ROLE_ADMIN"]}>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={handleApproveBill}
                disabled={isSubmitting}
                color="success"
              >
                {isSubmitting ? "Approving..." : "Approve Bill"}
              </Button>

              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleRejectBill}
                disabled={isSubmitting}
                color="error"
              >
                {isSubmitting ? "Rejecting..." : "Reject Bill"}
              </Button>
            </RoleBasedRender>
          </>
        )}

        {/* Edit button only for DRAFT bills */}
        {/* {billData.status === "DRAFT" && (
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            color={billData.billCategory === "RETAIL" ? "primary" : "secondary"}
          >
            Edit Bill
          </Button>
        )} */}
      </Box>

      {/* Item Details Dialog */}
      <BillItemDetailsDialog
        open={openItemView}
        onClose={handleCloseItemView}
        item={selectedItem}
        billCategory={billData?.billCategory}
      />
    </StyledPaper>
  );
}

export default SaleBillView;
